import { useState, useEffect } from "react";
import {
  apiAdminListUsers,
  apiAdminCreateUser,
  apiAdminUpdateUser,
  apiAdminDeleteUser,
} from "../data/api";
import type { ApiUser } from "../data/api";

import "./DrawingsDashboard.scss";

interface AdminPanelProps {
  onBack: () => void;
}

export const AdminPanel = ({ onBack }: AdminPanelProps) => {
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Create user form
  const [showForm, setShowForm] = useState(false);
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formName, setFormName] = useState("");
  const [formIsAdmin, setFormIsAdmin] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editPassword, setEditPassword] = useState("");

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await apiAdminListUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Auto-clear messages
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(""), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const resetForm = () => {
    setFormEmail("");
    setFormPassword("");
    setFormName("");
    setFormIsAdmin(false);
    setShowForm(false);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setError("");
    try {
      await apiAdminCreateUser({
        email: formEmail,
        password: formPassword,
        name: formName || undefined,
        is_admin: formIsAdmin,
      });
      resetForm();
      setSuccess("Usuário criado com sucesso");
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: string, email: string) => {
    if (
      !confirm(
        `Tem certeza que deseja excluir ${email}?\nTodos os desenhos deste usuário serão perdidos.`,
      )
    ) {
      return;
    }
    try {
      await apiAdminDeleteUser(id);
      setSuccess("Usuário excluído");
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const startEdit = (user: ApiUser) => {
    setEditingId(user.id);
    setEditName(user.name || "");
    setEditEmail(user.email);
    setEditIsAdmin(user.is_admin || false);
    setEditPassword("");
  };

  const handleSaveEdit = async () => {
    if (!editingId) {
      return;
    }
    try {
      const data: Record<string, any> = {
        name: editName,
        email: editEmail,
        is_admin: editIsAdmin,
      };
      if (editPassword) {
        data.password = editPassword;
      }
      await apiAdminUpdateUser(editingId, data);
      setEditingId(null);
      setSuccess("Usuário atualizado");
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-panel">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header__left">
          <button className="admin-back" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M11 1L4 8l7 7" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
            Voltar
          </button>
          <h2>Gerenciar Usuários</h2>
          <span className="admin-count">{users.length} usuários</span>
        </div>
        <button
          className={`admin-add-btn ${showForm ? "cancel" : ""}`}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Cancelar" : "+ Novo Usuário"}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="admin-message admin-message--error">
          <span>{error}</span>
          <button onClick={() => setError("")}>×</button>
        </div>
      )}
      {success && (
        <div className="admin-message admin-message--success">
          <span>{success}</span>
        </div>
      )}

      {/* Create Form */}
      {showForm && (
        <div className="admin-create-section">
          <h3>Novo Usuário</h3>
          <form className="admin-create-form" onSubmit={handleCreate}>
            <div className="admin-form-row">
              <div className="admin-form-field">
                <label>Nome</label>
                <input
                  type="text"
                  placeholder="Nome do usuário"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>
              <div className="admin-form-field">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="admin-form-row">
              <div className="admin-form-field">
                <label>Senha</label>
                <input
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <div className="admin-form-field admin-form-field--check">
                <label className="admin-toggle">
                  <input
                    type="checkbox"
                    checked={formIsAdmin}
                    onChange={(e) => setFormIsAdmin(e.target.checked)}
                  />
                  <span className="admin-toggle__slider" />
                  Administrador
                </label>
              </div>
            </div>
            <div className="admin-form-actions">
              <button
                type="button"
                className="admin-btn cancel"
                onClick={resetForm}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="admin-btn primary"
                disabled={formLoading}
              >
                {formLoading ? "Criando..." : "Criar Usuário"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      {loading ? (
        <div className="dashboard-loading">Carregando...</div>
      ) : (
        <div className="admin-users-grid">
          {users.map((user) => (
            <div
              key={user.id}
              className={`admin-user-card ${editingId === user.id ? "editing" : ""}`}
            >
              {editingId === user.id ? (
                /* Edit Mode */
                <div className="admin-user-card__edit">
                  <div className="admin-form-row">
                    <div className="admin-form-field">
                      <label>Nome</label>
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Nome"
                      />
                    </div>
                    <div className="admin-form-field">
                      <label>Email</label>
                      <input
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        placeholder="Email"
                      />
                    </div>
                  </div>
                  <div className="admin-form-row">
                    <div className="admin-form-field">
                      <label>Nova Senha</label>
                      <input
                        type="password"
                        value={editPassword}
                        onChange={(e) => setEditPassword(e.target.value)}
                        placeholder="Deixe vazio para manter"
                      />
                    </div>
                    <div className="admin-form-field admin-form-field--check">
                      <label className="admin-toggle">
                        <input
                          type="checkbox"
                          checked={editIsAdmin}
                          onChange={(e) =>
                            setEditIsAdmin(e.target.checked)
                          }
                        />
                        <span className="admin-toggle__slider" />
                        Administrador
                      </label>
                    </div>
                  </div>
                  <div className="admin-form-actions">
                    <button
                      className="admin-btn cancel"
                      onClick={() => setEditingId(null)}
                    >
                      Cancelar
                    </button>
                    <button
                      className="admin-btn primary"
                      onClick={handleSaveEdit}
                    >
                      Salvar
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  <div className="admin-user-card__info">
                    <div className="admin-user-card__avatar">
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="admin-user-card__details">
                      <span className="admin-user-card__name">
                        {user.name || "Sem nome"}
                      </span>
                      <span className="admin-user-card__email">
                        {user.email}
                      </span>
                    </div>
                    {user.is_admin && (
                      <span className="admin-user-card__badge">Admin</span>
                    )}
                  </div>
                  <div className="admin-user-card__actions">
                    <button
                      className="admin-btn edit"
                      onClick={() => startEdit(user)}
                    >
                      Editar
                    </button>
                    <button
                      className="admin-btn delete"
                      onClick={() => handleDelete(user.id, user.email)}
                    >
                      Excluir
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
